"use client"

import * as React from "react"
import { ChevronsUpDown, Plus, Settings, Building2 } from "lucide-react"
import { useRouter } from "next/navigation"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "../ui/sidebar"
import { Button } from "../ui/button"

interface Team {
  id: string
  name: string
  company: {
    name: string
  }
  slug: string
  isOwner: boolean
}

export function TeamSwitcher() {
  const { isMobile } = useSidebar()
  const router = useRouter()

  // Mock teams data - replace with actual data fetching
  const [teams, setTeams] = React.useState<Team[]>([])
  const [activeTeam, setActiveTeam] = React.useState<Team | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSwitching, setIsSwitching] = React.useState(false)

  React.useEffect(() => {
    // Load teams from localStorage or API
    if (typeof window !== "undefined") {
      const savedTeams = localStorage.getItem("AskFinz_teams")
      const savedActiveTeam = localStorage.getItem("AskFinz_active_team")

      if (savedTeams) {
        try {
          const parsedTeams = JSON.parse(savedTeams)
          setTeams(parsedTeams)

          if (savedActiveTeam) {
            const active = parsedTeams.find((t: Team) => t.id === savedActiveTeam)
            if (active) {
              setActiveTeam(active)
            } else if (parsedTeams.length > 0) {
              setActiveTeam(parsedTeams[0])
            }
          } else if (parsedTeams.length > 0) {
            setActiveTeam(parsedTeams[0])
          }
        } catch (e) {
          console.error("Failed to parse teams:", e)
        }
      } else {
        // Default team
        const defaultTeam: Team = {
          id: "default",
          name: "My Team",
          company: { name: "Personal" },
          slug: "my-team",
          isOwner: true
        }
        setTeams([defaultTeam])
        setActiveTeam(defaultTeam)
      }

      setIsLoading(false)
    }
  }, [])

  const handleTeamSettings = (teamSlug: string, e: React.MouseEvent) => {
    e.stopPropagation()
    router.push(`/teams/${encodeURIComponent(teamSlug)}/settings`)
  }

  const handleTeamSwitch = async (teamId: string) => {
    if (teamId === activeTeam?.id || isSwitching) return

    setIsSwitching(true)
    try {
      const team = teams.find(t => t.id === teamId)
      if (team) {
        setActiveTeam(team)
        if (typeof window !== "undefined") {
          localStorage.setItem("AskFinz_active_team", teamId)
        }
      }
    } catch (error) {
      console.error("[AskFinz] Failed to switch team:", error)
    } finally {
      setIsSwitching(false)
    }
  }

  if (isLoading || !activeTeam) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center">
            <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg group-data-[collapsible=icon]:size-6">
              <Building2 className="size-4 group-data-[collapsible=icon]:size-3.5" />
            </div>
            <div className="grid flex-1 text-left text-xs leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-medium">Loading...</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center h-10"
              disabled={isSwitching}
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg group-data-[collapsible=icon]:size-6">
                <Building2 className="size-4 group-data-[collapsible=icon]:size-3.5" />
              </div>
              <div className="grid flex-1 text-left text-xs leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{activeTeam.name}</span>
                <span className="truncate text-xs">{activeTeam.company.name}</span>
              </div>
              <ChevronsUpDown className="ml-auto group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">Teams</DropdownMenuLabel>
            {teams.map((team, index) => (
              <DropdownMenuItem
                key={team.id}
                onClick={() => handleTeamSwitch(team.id)}
                className="gap-2 p-2"
                disabled={team.id === activeTeam.id || isSwitching}
              >
                <div className="flex size-6 items-center justify-center rounded-md border">
                  <Building2 className="size-3.5 shrink-0" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{team.name}</div>
                  <div className="text-xs text-muted-foreground">{team.company.name}</div>
                </div>
                {team.isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => handleTeamSettings(team.slug, e)}
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                )}
                <DropdownMenuShortcut>?{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 p-2" onClick={() => {/* Add team dialog */ }}>
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <Plus className="size-4" />
              </div>
              <div className="text-muted-foreground font-medium">Add team</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

